import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Container, Typography, Box, Tabs, Tab, Paper } from '@mui/material';
import { Business, Group, Settings, AutoAwesome, Assessment, UploadFile } from '@mui/icons-material';
import CompanyManagement from '../pages/CompanyManagement';
import TeamManagement from '../pages/TeamManagement';
import AuditTemplateManagement from '../pages/AuditTemplateManagement';
import TemplateEditorPage from '../pages/TemplateEditorPage';
import { BillingSettings } from './Billing';

const AdminPortal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Determine which tab is active based on current path or query param
  const getActiveTab = () => {
    // Check for ?tab=settings query param (used by billing redirects)
    const tabParam = searchParams.get('tab');
    if (tabParam === 'settings') return 5;
    
    if (location.pathname.includes('/admin/companies')) return 0;
    if (location.pathname.includes('/admin/team')) return 1;
    if (location.pathname.includes('/admin/templates')) return 2;
    if (location.pathname.includes('/ai-builder/excel-import')) return 4;
    if (location.pathname.includes('/ai-builder')) return 3;
    if (location.pathname.includes('/admin/settings')) return 5;
    return 0;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate('/admin/companies');
        break;
      case 1:
        navigate('/admin/team');
        break;
      case 2:
        navigate('/admin/templates');
        break;
      case 3:
        navigate('/ai-builder');
        break;
      case 4:
        navigate('/ai-builder/excel-import');
        break;
      case 5:
        navigate('/admin/settings');
        break;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Administration
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your consulting business, team members, and client companies
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={getActiveTab()} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<Business />} 
            label="Client Companies" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<Group />} 
            label="Team Members" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<Assessment />} 
            label="Review Templates" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<AutoAwesome />} 
            label="AI Template Builder" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<UploadFile />} 
            label="Import from Excel" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<Settings />} 
            label="Settings" 
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
      </Paper>

      <Routes>
        <Route path="companies" element={<CompanyManagement />} />
        <Route path="team" element={<TeamManagement />} />
        <Route path="templates" element={<AuditTemplateManagement />} />
        <Route path="templates/edit/:templateId" element={<TemplateEditorPage />} />
        <Route path="templates/view/:templateId" element={<TemplateEditorPage />} />
        <Route path="settings" element={
          <Box>
            <Typography variant="h5" gutterBottom>
              Billing & Settings
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Manage your subscription, credits, and business settings
            </Typography>
            <BillingSettings />
          </Box>
        } />
        <Route path="" element={<Navigate to="companies" replace />} />
      </Routes>
    </Container>
  );
};

export default AdminPortal; 